// Handles opening the text modal and populating it with sidecar text content.
// Assumes sidecar plain text or markdown-lite (first line title if starts with '#').

const textModal = {
  cache: new Map(), // src -> { title, subtitle, html }
  lastTrigger: null,
  init() {
    this.$modal = document.querySelector('.text-modal');
    if (!this.$modal) return;
    this.$content = this.$modal.querySelector('.modal-content');
  this.$title = this.$modal.querySelector('.modal-title');
    this.$subtitle = this.$modal.querySelector('.modal-subtitle');
    this.$body = this.$modal.querySelector('.modal-body');
    this.$close = this.$modal.querySelector('.close');

    // Delegate clicks from pages container for triggers
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.text-modal-trigger');
      if (!btn) return;
      e.preventDefault();
      const src = btn.getAttribute('data-text-src');
      if (!src) return;
      this.lastTrigger = btn;
      this.openFromSource(src, btn);
    });

    // Close button
    if (this.$close) {
      this.$close.setAttribute('role', 'button');
      this.$close.setAttribute('tabindex', '0');
      this.$close.addEventListener('click', () => this.close());
      this.$close.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.close(); }
      });
    }
  },
  async openFromSource(src, triggerEl) {
    // If already open with same src, just focus modal
    if (this.$modal.classList.contains('open') && this.$modal.dataset.currentSrc === src) {
      this.focusModal();
      return;
    }

    // If another dropdown open (score/chapters), close them first
    document.querySelectorAll('.nav-dropdown.open').forEach(d => {
      if (d !== this.$modal) {
        d.classList.remove('open', 'above');
        d.setAttribute('aria-hidden', 'true');
        d.setAttribute('aria-modal', 'false');
        // Also collapse any triggers aria-expanded
        const id = d.id;
        if (id === 'score-dropdown') {
          const wrapper = document.querySelector('.nav-score-wrapper');
          if (wrapper) wrapper.setAttribute('aria-expanded', 'false');
        } else if (id === 'chapters-dropdown') {
          const wrapper = document.querySelector('.nav-chapters-wrapper');
          if (wrapper) wrapper.setAttribute('aria-expanded', 'false');
        }
      }
    });

    this.showLoadingState();

    let parsed;
    if (this.cache.has(src)) {
      parsed = this.cache.get(src);
    } else {
      try {
        const res = await fetch(src);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const text = await res.text();
        parsed = this.parseText(text, src);
        this.cache.set(src, parsed);
      } catch (err) {
        parsed = { title: 'Error', subtitle: '', html: `<p>Failed to load text: ${err.message}</p>` };
      }
    }

    this.applyContent(parsed, src);
    this.open();
    // Return focus to modal so screen reader announces content
    this.focusModal();
  },
  showLoadingState() {
    if (!this.$body) return;
    // Hide static title/subtitle during loading; we'll render inline later
    if (this.$title) { this.$title.textContent = ''; this.$title.style.display = 'none'; }
    if (this.$subtitle) { this.$subtitle.textContent = ''; this.$subtitle.style.display = 'none'; }
    this.$body.innerHTML = '<p class="loading">Fetching content…</p>';
  },
  parseText(raw, src) {
    const lines = raw.replace(/\r\n?/g, '\n').split('\n');
    let seenMainTitle = false;
    let subtitle = '';
    const blocks = []; // sequence preserving order
    let buf = [];

    lines.forEach(line => {
      const trimmed = line.trim();
      if (!trimmed) {
        if (buf.length) { blocks.push({ type: 'p', text: buf.join(' ').trim() }); buf = []; }
        return;
      }
      if (/^#\s+/.test(trimmed)) {
        const heading = trimmed.replace(/^#\s+/, '').trim();
        // flush any paragraph content before inserting heading
        if (buf.length) { blocks.push({ type: 'p', text: buf.join(' ').trim() }); buf = []; }
        // First # becomes a primary title inline; subsequent # become secondary titles inline
        if (!seenMainTitle) { blocks.push({ type: 'title', text: heading }); seenMainTitle = true; }
        else { blocks.push({ type: 'secondaryTitle', text: heading }); }
        return;
      }
      if (!subtitle && /^##\s+/.test(trimmed)) {
        subtitle = trimmed.replace(/^##\s+/, '').trim();
        // Insert subtitle inline where encountered
        if (buf.length) { blocks.push({ type: 'p', text: buf.join(' ').trim() }); buf = []; }
        blocks.push({ type: 'subtitle', text: subtitle });
        return;
      }
      buf.push(trimmed);
    });
    if (buf.length) { blocks.push({ type: 'p', text: buf.join(' ').trim() }); }

    const applyItalics = (str) => {
      const safe = this.escapeHTML(str);
      // Convert *text* to <em>text</em> after escaping
      return safe.replace(/\*(.*?)\*/g, '<em>$1</em>');
    };
    const html = blocks.map(b => {
      if (b.type === 'title') return `<div class=\"modal-title\">${applyItalics(b.text)}</div>`;
      if (b.type === 'secondaryTitle') return `<div class=\"modal-title secondary\">${applyItalics(b.text)}</div>`;
      if (b.type === 'subtitle') return `<div class=\"modal-subtitle\">${applyItalics(b.text)}</div>`;
      return `<p>${applyItalics(b.text)}</p>`;
    }).join('\n');
    return { html };
  },
  escapeHTML(str) {
    return str.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  },
  applyContent(parsed, src) {
    // Always hide static title/subtitle; content renders inline in body
    if (this.$title) { this.$title.textContent = ''; this.$title.style.display = 'none'; }
    if (this.$subtitle) { this.$subtitle.textContent = ''; this.$subtitle.style.display = 'none'; }
    if (this.$body) this.$body.innerHTML = parsed.html || '<p>No content.</p>';
    this.$modal.dataset.currentSrc = src;
  },
  open() {
    if (!this.$modal) return;
    this.$modal.classList.add('open');
    this.$modal.setAttribute('aria-hidden', 'false');
    this.$modal.setAttribute('aria-modal', 'true');
  },
  close() {
    if (!this.$modal) return;
    this.$modal.classList.remove('open', 'above');
    this.$modal.setAttribute('aria-hidden', 'true');
    this.$modal.setAttribute('aria-modal', 'false');
    if (this.lastTrigger) {
      this.lastTrigger.focus();
    }
  },
  focusModal() {
    // Focus first focusable element inside modal (close button preferred)
    const focusEl = this.$close || this.$title || this.$modal;
    if (focusEl) focusEl.focus();
  }
};

export default textModal;
